import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import '../styles/cookieconsent.css';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const CookieConsent = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const updateAnalyticsConsent = (allow: boolean) => {
    if (allow) {
      window.gtag('config', 'G-YWMFTCVZNQ');
    }
  };

  useEffect(() => {
    const createCookieConsent = () => {
      const cookieConsent = document.createElement('div');
      cookieConsent.className = `cc-window ${theme}`;
      
      const message = document.createElement('div');
      message.className = 'cc-message';
      message.textContent = language === 'en'
        ? 'We use cookies to enhance your experience and analyze site usage. By continuing to visit this site you agree to our use of cookies.'
        : 'Nous utilisons des cookies pour améliorer votre expérience et analyser l\'utilisation du site. En continuant à visiter ce site, vous acceptez notre utilisation des cookies.';
      
      const buttons = document.createElement('div');
      buttons.className = 'cc-buttons';
      
      const allowButton = document.createElement('button');
      allowButton.className = 'cc-btn cc-allow';
      allowButton.textContent = language === 'en' ? 'Accept All' : 'Tout Accepter';
      
      const settingsButton = document.createElement('button');
      settingsButton.className = 'cc-btn cc-settings';
      settingsButton.textContent = language === 'en' ? 'Customize' : 'Personnaliser';
      
      buttons.appendChild(settingsButton);
      buttons.appendChild(allowButton);
      
      cookieConsent.appendChild(message);
      cookieConsent.appendChild(buttons);
      
      allowButton.addEventListener('click', () => {
        document.cookie = 'cookieconsent_status=allow; max-age=31536000; path=/';
        updateAnalyticsConsent(true);
        cookieConsent.remove();
      });
      
      settingsButton.addEventListener('click', () => {
        showSettingsModal();
      });
      
      document.body.appendChild(cookieConsent);
    };
    
    const showSettingsModal = () => {
      const overlay = document.createElement('div');
      overlay.className = 'cc-overlay';
      
      const modal = document.createElement('div');
      modal.className = `cc-settings-modal ${theme}`;
      
      modal.innerHTML = `
        <h2 class="cc-settings-modal-title">${language === 'en' ? 'Cookie Preferences' : 'Préférences des Cookies'}</h2>
        
        <div class="cc-settings-block">
          <h3 class="cc-settings-block-title">${language === 'en' ? 'Essential Cookies' : 'Cookies Essentiels'}</h3>
          <p class="cc-settings-block-description">
            ${language === 'en' 
              ? 'These cookies are necessary for the website to function and cannot be switched off.'
              : 'Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés.'}
          </p>
        </div>
        
        <div class="cc-settings-block">
          <h3 class="cc-settings-block-title">${language === 'en' ? 'Analytics Cookies' : 'Cookies Analytiques'}</h3>
          <p class="cc-settings-block-description">
            ${language === 'en'
              ? 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.'
              : 'Ces cookies nous permettent de compter les visites et les sources de trafic pour mesurer et améliorer les performances de notre site.'}
          </p>
          <div class="cc-toggle">
            <input type="checkbox" id="analytics-toggle" class="cc-toggle-checkbox">
            <label for="analytics-toggle">${language === 'en' ? 'Enable Analytics' : 'Activer les Analyses'}</label>
          </div>
        </div>
        
        <div class="cc-modal-buttons">
          <button class="cc-btn cc-settings" id="cc-save-settings">
            ${language === 'en' ? 'Save Preferences' : 'Enregistrer les Préférences'}
          </button>
        </div>
      `;
      
      document.body.appendChild(overlay);
      document.body.appendChild(modal);
      
      const analyticsToggle = modal.querySelector('#analytics-toggle') as HTMLInputElement;
      const saveButton = modal.querySelector('#cc-save-settings');
      
      const cookieStatus = document.cookie.split(';').find(row => row.trim().startsWith('cookieconsent_status='));
      if (cookieStatus && cookieStatus.includes('analytics')) {
        analyticsToggle.checked = true;
      }
      
      saveButton?.addEventListener('click', () => {
        const status = analyticsToggle.checked ? 'allow_analytics' : 'essential';
        document.cookie = `cookieconsent_status=${status}; max-age=31536000; path=/`;
        updateAnalyticsConsent(analyticsToggle.checked);
        overlay.remove();
        modal.remove();
        document.querySelector('.cc-window')?.remove();
      });
      
      overlay.addEventListener('click', () => {
        overlay.remove();
        modal.remove();
      });
    };
    
    const hasConsent = document.cookie.split(';').some(row => row.trim().startsWith('cookieconsent_status='));
    
    if (!hasConsent) {
      createCookieConsent();
    } else {
      // Check and update consent based on stored cookie
      const cookieStatus = document.cookie.split(';').find(row => row.trim().startsWith('cookieconsent_status='));
      if (cookieStatus) {
        const hasAnalyticsConsent = cookieStatus.includes('allow') || cookieStatus.includes('analytics');
        updateAnalyticsConsent(hasAnalyticsConsent);
      }
    }
  }, [language, theme]);

  return null;
};

export default CookieConsent; 